import React, { useEffect, useState } from "react";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import {
  Box,
  IconButton,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Calculate,
  Edit,
  History,
  HistoryEdu,
  Info,
  PublishedWithChanges,
  SwapHoriz,
  Undo,
} from "@mui/icons-material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  iconSize,
  margin,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import OverflowTooltip from "controls/global/overflow-tooltip";
import TextInputField from "controls/global/text-input-field/TextInputField";
import { showToast } from "controls/global/stewart-toast";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { BookedRow } from "utils/data/enum";
import { bulkAdjustPolicyHistory } from "utils/api/policyHistoryApi";
import {
  bookedPremium,
  bookedRemittance,
  expectedRemittance,
  isSplitMismatch,
  REMITTANCE_OVER_PREMIUM_FIELD_ERROR,
  SPLIT_MISMATCH_FIELD_ERROR,
  toNumOrNull,
} from "./helper";
import PercentNumberFormat from "../shared/PercentNumberFormat";
// import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(90vw)",
    minWidth: "calc(90vw)",
    display: "flex",
    flexDirection: "column",
    padding: `${padding.xlarge21_5} ${padding.xlarge22} ${padding.xlarge22} ${padding.xlarge22}`,
    overflow: "hidden",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: colors.blue19,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.blue15,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDescriptionTypography = styled(Typography)({
  color: colors.grey22,
  fontSize: fontSize.small,
});

const StyledDialogContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
});

const AdjustmentTypeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
});

const ToggleLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey27,
  textTransform: "uppercase",
  letterSpacing: "0.8px",
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
  background: colors.blue24,
  border: `1px solid ${colors.blue25}`,
  borderRadius: borderRadius.large,
  padding: padding.xsmall1,
  gap: gaps.xsmall2,
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: `${borderRadius.large} !important`,
    color: colors.blue15,
    fontWeight: fontWeight.bold2,
    fontSize: fontSize.small,
    textTransform: "none",
    padding: `${padding.small} ${padding.large1}`,
    display: "flex",
    gap: gaps.xsmall1,
    "&.Mui-selected": {
      background: colors.white,
      color: colors.blue15,
      fontWeight: fontWeight.bold2,
      boxShadow: "0 1px 3px rgba(20,40,70,0.12), 0 0 0 1px rgba(33,93,128,0.18)",
      "&:hover": { background: colors.white },
    },
    "&:hover": { background: "transparent" },
  },
});

const SplitToolbar = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  columnGap: gaps.large1,
  rowGap: gaps.small1,
  background: colors.blue26,
  border: `1px solid ${colors.blue27}`,
  borderRadius: borderRadius.medium,
  padding: `${padding.large1} ${padding.xlarge}`,
});

const ToolbarBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
});

const FieldLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const OriginalSplitValue = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold2,
  color: colors.grey08,
});

const HelperText = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.grey11,
  flex: 1,
  minWidth: "200px",
  textAlign: "right",
});


const CardsGrid = styled(Box)({
  display: "grid",
  gap: gaps.medium0,
  alignItems: "stretch",
});

const AdjCard = styled(Box)({
  border: `1px solid ${colors.grey36}`,
  borderRadius: borderRadius.medium,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: colors.white,
});

const CardHeader = styled(Box)({
  padding: `${padding.medium1} ${padding.large1}`,
  borderBottom: `1px solid ${colors.grey37}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gaps.small1,
  minHeight: "46px",
});

const CardEyebrow = styled(Box)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: gaps.xsmall2_5,
  "& .MuiSvgIcon-root": { fontSize: fontSize.medium },
});

const CardTag = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  padding: `${padding.xsmall0} ${padding.small1}`,
  borderRadius: borderRadius.xsmall1,
  whiteSpace: "nowrap",
});

const CardBody = styled(Box)({
  padding: `${padding.medium2} ${padding.large1} ${padding.large1}`,
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

const CardMeta = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.small0,
  padding: `${padding.xsmall} ${padding.zero} ${padding.medium1}`,
  marginBottom: margin.small,
  borderBottom: `1px solid ${colors.grey30}`,
});

const MetaRow = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: gaps.small1,
});

const MetaLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey28,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
});

const MetaValue = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.grey29,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
});

const CardFieldRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gaps.small1,
  padding: `${padding.small1} ${padding.zero}`,
  minHeight: "38px",
  borderBottom: `1px solid ${colors.grey31}`,
  "&:last-child": { borderBottom: "none" },
});

const CardFieldLabel = styled(Typography)({
  fontSize: fontSize.xsmall1,
  fontWeight: fontWeight.bold2,
  color: colors.grey28,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
});

const MoneyInputWrapper = styled(Box)({
  width: "140px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
  "& .errorMessage": { display: "none" },
});

const ErrorSlot = styled(Box)({
  minHeight: "14px",
});

const MoneyDisplay = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold2,
  minWidth: "120px",
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
});


const ReasonSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.small1,
});

const ReasonLabel = styled("label")({
  fontSize: fontSize.xsmall1,
  fontWeight: fontWeight.bold2,
  color: colors.grey29,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: gaps.xsmall2_5,
  "& .MuiSvgIcon-root": {
    fontSize: fontSize.medium,
    color: colors.grey28,
  },
});

const ReasonTextArea = styled("textarea")({
  width: "100%",
  border: `1px solid ${colors.grey32}`,
  borderRadius: borderRadius.small1,
  padding: `${padding.small2} ${padding.medium1}`,
  fontSize: fontSize.small1,
  fontFamily: "inherit",
  background: colors.grey33,
  resize: "vertical",
  lineHeight: 1.5,
  color: "inherit",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.12s, box-shadow 0.12s",
  "&:focus": {
    borderColor: colors.blue15,
    boxShadow: "0 0 0 3px rgba(33,93,128,0.10)",
    background: colors.white,
  },
});

const NetChangeBar = styled(Box)({
  marginTop: margin.medium1,
  padding: `${padding.small2} ${padding.medium2}`,
  background: colors.grey34,
  border: `1px solid ${colors.grey32}`,
  borderRadius: borderRadius.small1,
  fontSize: fontSize.xsmall,
  color: colors.grey29,
  display: "flex",
  gap: gaps.small1,
  flexWrap: "wrap",
});

const NetValue = styled("span")({
  fontVariantNumeric: "tabular-nums",
  fontWeight: fontWeight.bold1,
});

const DialogFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: gaps.small1,
  marginTop: margin.large,
  paddingTop: padding.large1,
  borderTop: `1px solid ${colors.grey09}`,
  marginBottom: margin.xlarge1,
});

// Validation summary in the footer.
const FooterError = styled(Box)({
  flex: 1,
  color: colors.red02,
  fontSize: fontSize.small,
  textAlign: "left",
});

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <OverflowTooltip
      tooltip={message}
      text={message}
      placement="bottom-start"
      style={{
        color: colors.red02,
        fontSize: fontSize.xsmall,
        fontStyle: "italic",
        textAlign: "left",
        maxWidth: "100%",
      }}
    />
  ) : null;

const netColor = (v: number): string =>
  v < 0 ? colors.red03 : v > 0 ? colors.green01 : colors.grey29;

const DESCRIPTION_TEXT =
  "Original stays in History. A reversal zeros it out; the new record carries replacement dollars into Close Queue.";
const HELPER_TEXT =
  "UW Remittance recalculates from the new split. Premium and Liability default to original.";
const ENTER_NEW_SPLIT_FIRST = "Enter a New Split % first";
// const STRAFE_CALC_MESSAGE =
//   "STRAFE calc complete. Retention computed at $PERCENT% of Premium; Premium and Liability unchanged.";
const SUBMIT_SUCCESS =
  "Adjustment submitted. New records will appear in Close Queue.";
const SUBMIT_ERROR = "Failed to submit the adjustment. Please try again.";
// Without a PolicyHistoryID there's nothing for the API to adjust.
const NO_RECORD_ERROR = "This record can't be adjusted.";
// Footer wording mirrors BulkAdjustModal's, in the singular.
const REMITTANCE_OVER_PREMIUM_ERROR =
  "UW Remittance is greater than the New Premium. Correct it to continue.";
const SPLIT_MISMATCH_ERROR =
  "New Split % doesn't match the New Premium and UW Remittance. Correct it to continue.";

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null) return "$0.00";
  const abs = Math.abs(v).toFixed(2);
  return v < 0 ? `-$${abs}` : `$${abs}`;
};

const moneyInputProps = {
  step: "0.01",
  min: "0",
  style: { textAlign: "right" as const },
};

export interface AdjustmentValues {
  newSplitPercent: string;
  newLiability: string;
  newPremium: string;
  newRetention: string;
  reason: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  record: PolicyManagementRecord;
  originalSplitPercent?: number;
  initialValues?: Partial<AdjustmentValues>;
  onSubmit?: (values: AdjustmentValues, isReversalOnly: boolean) => void;
};

const getDefaultValues = (
  initialValues?: Partial<AdjustmentValues>,
): AdjustmentValues => ({
  newSplitPercent: "",
  newLiability: "",
  newPremium: "",
  newRetention: "",
  reason: "",
  ...initialValues,
});

const isValidAmount = (val: string | undefined) =>
  !val || (!isNaN(Number(val)) && Number(val) >= 0);

const adjustmentSchema = yup.object({
  newSplitPercent: yup
    .string(),
  newLiability: yup
    .string()
    .test("valid-amount", "Must be a valid non-negative amount", isValidAmount),
  newPremium: yup
    .string()
    .test("valid-amount", "Must be a valid non-negative amount", isValidAmount),
  newRetention: yup
    .string()
    .test("valid-amount", "Must be a valid non-negative amount", isValidAmount),
  reason: yup.string(),
});

const AdjustmentModal = ({
  open,
  onClose,
  record,
  onRefresh,
  originalSplitPercent = 0,
  initialValues,
  onSubmit,
}: Props) => {
  const [isReversalOnly, setIsReversalOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [footerError, setFooterError] = useState("");

  const methods = useForm<AdjustmentValues>({
    mode: "onBlur",
    defaultValues: getDefaultValues(initialValues),
    resolver: yupResolver(adjustmentSchema),
  });
  const {
    reset,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    formState,
  } = methods;

  const [newSplitPercent, newLiability, newPremium, newRetention, reason] =
    watch([
      "newSplitPercent",
      "newLiability",
      "newPremium",
      "newRetention",
      "reason",
    ]);

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(initialValues));
      setIsReversalOnly(false);
      setIsSaving(false);
      setFooterError("");
    }
  }, [open]);

  const bookedSide = record.bookedRow ?? BookedRow.CALCULATED;

  const origSplitPercent =
    originalSplitPercent || record.OriginalRemitSplitPct || 0;

  const origLiability = record.LiabilityAmount ?? 0;
  const origPremium = bookedPremium(record);
  const origRetention = bookedRemittance(record);

  const revLiability = origLiability !== 0 ? -origLiability : 0;
  const revPremium = origPremium !== 0 ? -origPremium : 0;
  const revRetention = origRetention !== 0 ? -origRetention : 0;

  const parsedSplit = Number(newSplitPercent) || 0;
  const parsedNewLiab = Number(newLiability) || 0;
  const parsedNewPrem = Number(newPremium) || 0;
  const parsedNewRet = Number(newRetention) || 0;
  const hasNewValues =
    !isReversalOnly &&
    (parsedNewLiab !== 0 || parsedNewPrem !== 0 || parsedNewRet !== 0);
  const netRows = [
    { label: "Liability", value: parsedNewLiab - origLiability },
    { label: "Premium", value: parsedNewPrem - origPremium },
    { label: "UW Remittance", value: parsedNewRet - origRetention },
  ];

  const premiumBase = parsedNewPrem || origPremium;

  const remittanceExceedsPremium =
    !isReversalOnly && parsedNewRet > premiumBase;

  const splitMismatch = () => {
    if (isReversalOnly) return false;
    if (!newSplitPercent?.trim() || !newRetention?.trim()) return false;
    return isSplitMismatch(parsedSplit, premiumBase, parsedNewRet);
  };

  // Apply re-evaluates from scratch.
  useEffect(() => {
    clearErrors(["newSplitPercent", "newPremium", "newRetention"]);
    setFooterError("");
  }, [newSplitPercent, newPremium, newRetention, isReversalOnly, clearErrors]);

  const canSubmit =
    (isReversalOnly || hasNewValues) &&
    !remittanceExceedsPremium &&
    !isSaving;

  function handleOnClose() {
    onClose && onClose();
  }

  // The input caps entry at 100, so no clamp is needed here.
  const handleCalculate = () => {
    const split = Number(getValues("newSplitPercent")) || 0;
    if (split <= 0) {
      showToast({ title: ENTER_NEW_SPLIT_FIRST, type: "info" });
      return;
    }

    const retention = (origPremium * split) / 100;
    setValue("newRetention", retention.toFixed(2));
    setValue("newLiability", origLiability.toString());
    setValue("newPremium", origPremium.toString());
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSaving) return;

    const policyHistoryId = record.PolicyHistoryID ?? 0;
    if (policyHistoryId <= 0) {
      showToast({ message: NO_RECORD_ERROR, type: "error" });
      return;
    }

    if (splitMismatch()) {
      const expected = expectedRemittance(premiumBase, parsedSplit);
      // All three fields go red, but only UW Remittance carries the text —
      setError("newSplitPercent", { message: "" });
      setError("newPremium", { message: "" });
      setError("newRetention", {
        message: SPLIT_MISMATCH_FIELD_ERROR(parsedSplit, premiumBase, expected),
      });
      setFooterError(SPLIT_MISMATCH_ERROR);
      return;
    }

    setIsSaving(true);
    const isAdjusted = await bulkAdjustPolicyHistory({
      AdjustmentReason: reason?.trim() ?? "",
      Adjustments: [
        {
          PolicyHistoryID: policyHistoryId,
          IsReversalOnly: isReversalOnly,
          ...(isReversalOnly
            ? {}
            : {
                NewLiabilityAmount: toNumOrNull(newLiability),
                NewPremiumAmount: toNumOrNull(newPremium),
                NewRetentionAmount: toNumOrNull(newRetention),
              }),
          NewSplitPct: toNumOrNull(newSplitPercent),
        },
      ],
    });
    setIsSaving(false);

    if (!isAdjusted) {
      showToast({ message: SUBMIT_ERROR, type: "error" });
      return;
    }

    showToast({ message: SUBMIT_SUCCESS, type: "success" });
    onSubmit && onSubmit(getValues(), isReversalOnly);
    handleOnClose();
    onRefresh && onRefresh();
  };

  return (
    <StyledDialog open={open}>
      <FormProvider {...methods}>
        <StyledDialogTitle>
          <IconContainer>
            <PublishedWithChanges />
          </IconContainer>
          <Typography>Adjust Closed Record</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleOnClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <div>
            <Typography color={colors.grey22} fontSize={fontSize.small} marginTop={margin.small2}>
              Policy{" "}
              <Typography component="span" fontWeight={600}>
                {record.PolicyNumber || ""}
              </Typography>
            </Typography>
            <Box sx={{ marginBottom: margin.xlarge }} />
            <StyledDescriptionTypography>
              <Info color="primary" /> {DESCRIPTION_TEXT}
            </StyledDescriptionTypography>
          </div>

          <AdjustmentTypeRow>
            <ToggleLabel>Adjustment Type</ToggleLabel>
            <StyledToggleButtonGroup
              value={isReversalOnly}
              exclusive
              onChange={(_, val) => val !== null && setIsReversalOnly(val)}
            >
              <ToggleButton value={false}>
                <SwapHoriz fontSize="small" />
                Full Adjustment
              </ToggleButton>
              <ToggleButton value={true}>
                <Undo fontSize="small" />
                Reversal Only
              </ToggleButton>
            </StyledToggleButtonGroup>
          </AdjustmentTypeRow>

        <ToggleLabel>REMIT SPLIT CALCULATOR</ToggleLabel>
          <SplitToolbar>
            <ToolbarBlock>
              <FieldLabel>Original Split %</FieldLabel>
              <OriginalSplitValue>{origSplitPercent.toFixed(2)}%</OriginalSplitValue>
            </ToolbarBlock>

            <MoneyInputWrapper>
              <TextInputField
                label="NEW SPLIT %"
                name="newSplitPercent"
                size="small"
                inputProps={{ inputComponent: PercentNumberFormat as any, suffix: " %" }}
              />
              <FieldError message={formState.errors.newSplitPercent?.message} />
            </MoneyInputWrapper>

            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<Calculate />}
              onClick={handleCalculate}
              disabled={isReversalOnly}
            >
              Calculate
            </ActionButton>

            <HelperText>{HELPER_TEXT}</HelperText>
          </SplitToolbar>

          <Box>
            <CardsGrid
              sx={{
                gridTemplateColumns: isReversalOnly ? "1fr 1fr" : "1fr 1fr 1fr",
              }}
            >
              <AdjCard sx={{ borderColor: colors.grey36 }}>
                <CardHeader sx={{ background: colors.grey34, borderBottomColor: colors.grey37 }}>
                  <CardEyebrow sx={{ color: colors.grey29 }}>
                    <History />
                    Original
                  </CardEyebrow>
                  <CardTag sx={{ background: colors.grey35, color: colors.grey29 }}>
                    In History
                  </CardTag>
                </CardHeader>
                <CardBody>
                  <CardMeta>
                    <MetaRow>
                      <MetaLabel>Period</MetaLabel>
                      <MetaValue>{record.AccountingPeriod || "—"}</MetaValue>
                    </MetaRow>
                    <MetaRow>
                      <MetaLabel>Booked Row</MetaLabel>
                      <MetaValue>
                        {bookedSide === "reported" ? "Reported" : "Calculated"}
                      </MetaValue>
                    </MetaRow>
                  </CardMeta>
                  <CardFieldRow>
                    <CardFieldLabel>Liability</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.grey08 }}>
                      {fmtMoney(origLiability)}
                    </MoneyDisplay>
                  </CardFieldRow>
                  <CardFieldRow>
                    <CardFieldLabel>Premium</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.grey08 }}>
                      {fmtMoney(origPremium)}
                    </MoneyDisplay>
                  </CardFieldRow>
                  <CardFieldRow>
                    <CardFieldLabel>UW Remittance</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.grey08 }}>
                      {fmtMoney(origRetention)}
                    </MoneyDisplay>
                  </CardFieldRow>
                </CardBody>
              </AdjCard>

              {/* REVERSAL — auto-computed, read-only */}
              <AdjCard sx={{ borderColor: colors.yellow02, background: colors.yellow03 }}>
                <CardHeader sx={{ background: colors.yellow04, borderBottomColor: colors.yellow02 }}>
                  <CardEyebrow sx={{ color: colors.yellow01 }}>
                    <Undo />
                    Reversal
                  </CardEyebrow>
                  <CardTag sx={{ background: colors.yellow05, color: colors.yellow06 }}>
                    Auto-Computed
                  </CardTag>
                </CardHeader>
                <CardBody>
                  <CardMeta sx={{ borderBottomColor: colors.yellow07 }}>
                    <MetaRow>
                      <MetaLabel>Period</MetaLabel>
                      <MetaValue>(blank)</MetaValue>
                    </MetaRow>
                    <MetaRow>
                      <MetaLabel>Type</MetaLabel>
                      <MetaValue>Adjustment</MetaValue>
                    </MetaRow>
                  </CardMeta>
                  <CardFieldRow>
                    <CardFieldLabel>Liability</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.yellow06 }}>
                      {fmtMoney(revLiability)}
                    </MoneyDisplay>
                  </CardFieldRow>
                  <CardFieldRow>
                    <CardFieldLabel>Premium</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.yellow06 }}>
                      {fmtMoney(revPremium)}
                    </MoneyDisplay>
                  </CardFieldRow>
                  <CardFieldRow>
                    <CardFieldLabel>UW Remittance</CardFieldLabel>
                    <MoneyDisplay sx={{ color: colors.yellow06 }}>
                      {fmtMoney(revRetention)}
                    </MoneyDisplay>
                  </CardFieldRow>
                </CardBody>
              </AdjCard>

              {/* NEW RECORD — editable inputs, hidden in Reversal Only mode */}
              {!isReversalOnly && (
                <AdjCard sx={{ borderColor: colors.blue28 }}>
                  <CardHeader sx={{ background: colors.blue29, borderBottomColor: colors.blue28 }}>
                    <CardEyebrow sx={{ color: colors.blue15 }}>
                      <Edit />
                      New Record
                    </CardEyebrow>
                    <CardTag sx={{ background: colors.blue30, color: colors.blue31 }}>
                      Enter Dollars
                    </CardTag>
                  </CardHeader>
                  <CardBody>
                    <CardMeta sx={{ borderBottomColor: colors.blue32 }}>
                      <MetaRow>
                        <MetaLabel>Period</MetaLabel>
                        <MetaValue>Set on Close</MetaValue>
                      </MetaRow>
                      <MetaRow>
                        <MetaLabel>Type</MetaLabel>
                        <MetaValue>Adjustment</MetaValue>
                      </MetaRow>
                    </CardMeta>
                    <CardFieldRow>
                      <CardFieldLabel>Liability</CardFieldLabel>
                      <MoneyInputWrapper>
                        <TextInputField
                          name="newLiability"
                          size="small"
                          type="number"
                          placeholder="0.00"
                          inputProps={moneyInputProps}
                        />
                        <ErrorSlot>
                          <FieldError
                            message={formState.errors.newLiability?.message}
                          />
                        </ErrorSlot>
                      </MoneyInputWrapper>
                    </CardFieldRow>
                    <CardFieldRow>
                      <CardFieldLabel>Premium</CardFieldLabel>
                      <MoneyInputWrapper>
                        <TextInputField
                          name="newPremium"
                          size="small"
                          type="number"
                          placeholder="0.00"
                          inputProps={moneyInputProps}
                        />
                        <ErrorSlot>
                          <FieldError
                            message={formState.errors.newPremium?.message}
                          />
                        </ErrorSlot>
                      </MoneyInputWrapper>
                    </CardFieldRow>
                    <CardFieldRow>
                      <CardFieldLabel>UW Remittance</CardFieldLabel>
                      <MoneyInputWrapper>
                        <TextInputField
                          name="newRetention"
                          size="small"
                          type="number"
                          placeholder="0.00"
                          inputProps={moneyInputProps}
                        />
                        <ErrorSlot>
                          <FieldError
                            message={
                              remittanceExceedsPremium
                                ? REMITTANCE_OVER_PREMIUM_FIELD_ERROR(
                                    premiumBase,
                                  )
                                : formState.errors.newRetention?.message
                            }
                          />
                        </ErrorSlot>
                      </MoneyInputWrapper>
                    </CardFieldRow>
                  </CardBody>
                </AdjCard>
              )}
            </CardsGrid>

            {hasNewValues && (
              <NetChangeBar>
                <strong>Net effect (new − original):</strong>
                {netRows.map((row, i) => (
                  <span key={row.label}>
                    {i === 0 ? " " : ", "}{row.label}{" "}
                    <NetValue style={{ color: netColor(row.value) }}>
                      {fmtMoney(row.value)}
                    </NetValue>
                  </span>
                ))}
              </NetChangeBar>
            )}
          </Box>

          <ReasonSection>
            <ReasonLabel>
              <HistoryEdu />
              Reason for Adjustment
              <Box
                component="span"
                sx={{
                  fontSize: fontSize.xsmall,
                  color: colors.grey28,
                  fontWeight: fontWeight.bold1,
                  letterSpacing: "0.3px",
                  textTransform: "none",
                  marginLeft: margin.xxsmall2,
                }}
              >
                (optional)
              </Box>
            </ReasonLabel>
            <ReasonTextArea
              rows={2}
              placeholder="Optional context, e.g. 'Premium overstated on closing statement; revised per agent's amendment dated 04/12/2026.'"
              value={reason}
              onChange={(e) => setValue("reason", e.target.value)}
            />
          </ReasonSection>

          <DialogFooter>
            {remittanceExceedsPremium && (
              <FooterError>{REMITTANCE_OVER_PREMIUM_ERROR}</FooterError>
            )}
            {!remittanceExceedsPremium && footerError && (
              <FooterError>{footerError}</FooterError>
            )}
            <ActionButton
              variant="outlined"
              color="secondary"
              disabled={isSaving}
              onClick={handleOnClose}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {isSaving
                ? "Applying..."
                : isReversalOnly
                  ? "Apply Reversal"
                  : "Apply Adjustment"}
            </ActionButton>
          </DialogFooter>
        </StyledDialogContent>
      </FormProvider>
    </StyledDialog>
  );
};

export default AdjustmentModal;
